import { computed, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue'

import { creativeWatchlistApi, type CreativeWatchlistApi } from '../api/CreativeWatchlistApi'
import {
  SaveMetaWatchlistFlow,
  type SaveMetaWatchlistOutcome,
} from '../flows/SaveMetaWatchlistFlow'
import type { MetaWatchlistDefinition } from '../models/definition.types'
import type {
  MetaDraftErrors,
  MetaDraftValue,
  MetaPage,
  MetaWatchlistDraft,
  WatchlistFormField,
} from '../models/watchlist.types'
import {
  META_MAX_CONFIGS,
  emptyMetaValues,
  metaFilterFields,
  setForm,
  validateMetaDraft,
} from '../service/metaWatchlist'

import { useWorkspaceAttribute } from '@/features/workspace-settings'

type MetaAsyncState = 'loading' | 'error' | 'ready'

export interface UseMetaWatchlist {
  state: Ref<MetaAsyncState>
  fields: Ref<WatchlistFormField[]>
  filterFields: ComputedRef<WatchlistFormField[]>
  pages: Ref<MetaPage[]>
  drafts: Ref<MetaWatchlistDraft[]>
  query: Ref<string>
  expandedUid: Ref<string | null>
  saving: Ref<boolean>
  hasSubmitted: Ref<boolean>
  loadErrorKey: Ref<string | null>
  saveErrorKey: Ref<string | null>
  addErrorKey: Ref<string | null>
  errors: ComputedRef<Record<string, MetaDraftErrors>>
  errorCount: ComputedRef<number>
  atCap: ComputedRef<boolean>
  canSave: ComputedRef<boolean>
  savedDefinitions: ComputedRef<MetaWatchlistDefinition[]>
  filteredPages: ComputedRef<MetaPage[]>
  load: () => Promise<void>
  resetDrafts: () => void
  setQuery: (value: string) => void
  addPage: (page?: MetaPage) => boolean
  removeDraft: (uid: string) => void
  toggleDraft: (uid: string) => void
  patchValue: (uid: string, key: string, value: MetaDraftValue) => void
  touchField: (uid: string, field: 'config_name' | 'page_id') => void
  save: () => Promise<SaveMetaWatchlistOutcome>
}

export function useMetaWatchlist(
  api: CreativeWatchlistApi = creativeWatchlistApi,
): UseMetaWatchlist {
  const attribute = useWorkspaceAttribute('meta_ads_library_config')
  const state = ref<MetaAsyncState>('loading')
  const fields = ref<WatchlistFormField[]>([])
  const pages = ref<MetaPage[]>([])
  const drafts = ref<MetaWatchlistDraft[]>([])
  const query = ref('')
  const expandedUid = ref<string | null>(null)
  const saving = ref(false)
  const hasSubmitted = ref(false)
  const loadErrorKey = ref<string | null>(null)
  const saveErrorKey = ref<string | null>(null)
  const addErrorKey = ref<string | null>(null)
  let sequence = 0
  let controller: AbortController | null = null

  const savedDefinitions = computed(() => attribute.value.value ?? [])
  const filterFields = computed(() => metaFilterFields(fields.value))
  const errors = computed(() =>
    Object.fromEntries(
      drafts.value
        .map((draft) => [draft.uid, validateMetaDraft(draft)] as const)
        .filter(([, value]) => Object.keys(value).length > 0),
    ),
  )
  const errorCount = computed(() => Object.keys(errors.value).length)
  const atCap = computed(() => drafts.value.length >= META_MAX_CONFIGS)
  const canSave = computed(() => state.value === 'ready' && !saving.value)
  const filteredPages = computed(() => {
    const needle = query.value.trim().toLocaleLowerCase()
    if (!needle) return []
    return pages.value.filter((page) =>
      `${page.page_id}+${page.page_name}`.toLocaleLowerCase().includes(needle),
    )
  })

  function nextUid(): string {
    sequence += 1
    return `meta-${sequence}`
  }

  function blankDraft(): MetaWatchlistDraft {
    return { uid: nextUid(), values: emptyMetaValues(fields.value), touched: {} }
  }

  function resetDrafts(): void {
    drafts.value = savedDefinitions.value.length
      ? savedDefinitions.value.map((definition) => setForm(definition, fields.value, nextUid()))
      : [blankDraft()]
    expandedUid.value = null
    query.value = ''
    hasSubmitted.value = false
    saveErrorKey.value = null
    addErrorKey.value = null
  }

  async function load(): Promise<void> {
    controller?.abort()
    const request = new AbortController()
    controller = request
    state.value = 'loading'
    loadErrorKey.value = null
    try {
      const [loadedFields, loadedPages] = await Promise.all([
        api.getMetaFormFields(request.signal),
        api.getMetaPageDetails(request.signal),
      ])
      if (request.signal.aborted) return
      fields.value = loadedFields
      pages.value = loadedPages
      state.value = 'ready'
      resetDrafts()
    } catch {
      if (request.signal.aborted) return
      state.value = 'error'
      loadErrorKey.value = 'workspaceDefinitions.watchlist.meta.error.load'
    }
  }

  function setQuery(value: string): void {
    query.value = value
    addErrorKey.value = null
  }

  function addPage(page?: MetaPage): boolean {
    addErrorKey.value = null
    if (atCap.value) {
      addErrorKey.value = 'workspaceDefinitions.watchlist.meta.error.cap'
      return false
    }
    const raw = query.value.trim()
    const pageId = page?.page_id ?? (/^\d+$/u.test(raw) ? raw : '')
    const pageName = page?.page_name ?? (pageId ? '' : raw)
    if (!pageId && !pageName) return false
    if (
      pageId &&
      drafts.value.some((draft) =>
        Array.isArray(draft.values.search_page_ids)
          ? draft.values.search_page_ids.includes(pageId)
          : false,
      )
    ) {
      addErrorKey.value = 'workspaceDefinitions.watchlist.meta.error.duplicate'
      return false
    }
    const draft = blankDraft()
    draft.values.config_name = pageName
    draft.values.search_page_ids = [pageId]
    const first = drafts.value[0]
    const firstIds = first?.values.search_page_ids
    const firstBlank =
      drafts.value.length === 1 &&
      !String(first?.values.config_name ?? '').trim() &&
      Array.isArray(firstIds) &&
      !String(firstIds[0] ?? '').trim()
    drafts.value = firstBlank ? [draft] : [draft, ...drafts.value]
    expandedUid.value = draft.uid
    query.value = ''
    return true
  }

  function removeDraft(uid: string): void {
    drafts.value = drafts.value.filter((draft) => draft.uid !== uid)
    if (expandedUid.value === uid) expandedUid.value = null
  }

  function toggleDraft(uid: string): void {
    expandedUid.value = expandedUid.value === uid ? null : uid
  }

  function patchValue(uid: string, key: string, value: MetaDraftValue): void {
    const draft = drafts.value.find((item) => item.uid === uid)
    if (!draft) return
    draft.values = { ...draft.values, [key]: value }
  }

  function touchField(uid: string, field: 'config_name' | 'page_id'): void {
    const draft = drafts.value.find((item) => item.uid === uid)
    if (draft) draft.touched = { ...draft.touched, [field]: true }
  }

  async function save(): Promise<SaveMetaWatchlistOutcome> {
    hasSubmitted.value = true
    saveErrorKey.value = null
    saving.value = true
    try {
      const outcome = await new SaveMetaWatchlistFlow(attribute).execute(drafts.value, fields.value)
      if (outcome.status === 'failed') {
        saveErrorKey.value = 'workspaceDefinitions.watchlist.meta.error.save'
      }
      return outcome
    } finally {
      saving.value = false
    }
  }

  watch(savedDefinitions, () => {
    if (state.value === 'ready' && !saving.value) resetDrafts()
  })
  void load()
  onScopeDispose(() => controller?.abort())

  return {
    state,
    fields,
    filterFields,
    pages,
    drafts,
    query,
    expandedUid,
    saving,
    hasSubmitted,
    loadErrorKey,
    saveErrorKey,
    addErrorKey,
    errors,
    errorCount,
    atCap,
    canSave,
    savedDefinitions,
    filteredPages,
    load,
    resetDrafts,
    setQuery,
    addPage,
    removeDraft,
    toggleDraft,
    patchValue,
    touchField,
    save,
  }
}
